import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import DayPicker from 'react-day-picker-old';
import { useHistory, useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';
import * as yup from 'yup';
import { ILocationState } from '../../../../models';
import { MastheadPortalId } from '../../../../models/AdminModels';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { useUserQuery } from '../../../../queries';
import {
	AccountFilterCriteriaProperty,
	AccountsAdminViewModel,
	IAccountFilterCriteria,
	QuickSightDashboardViewModel,
} from '../../../../viewmodels/AdminViewModels';
import { AutoCompleteSearchField } from '../../../../web/components/autocomplete/AutoCompleteSearchField';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../../web/components/Breadcrumb';
import { Checkbox } from '../../../../web/components/Checkbox';
import { DeprecatedSelect, ISelectOption, MultiSelect } from '../../../../web/components/DeprecatedSelect';
import { ExportConfirmationModal } from '../../../../web/components/ExportConfirmation';
import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from '../../../../web/components/Popover';
import { Portal } from '../../../../web/components/Portal';
import { CalendarDateIcon } from '../../../../web/components/svgs/icons/CalendarDateIcon';
import { ExportIcon } from '../../../../web/components/svgs/icons/ExportIcon';
import { QuickSightIcon } from '../../../../web/components/svgs/icons/QuickSightIcon';
import { SearchIcon } from '../../../../web/components/svgs/icons/SearchIcon';
import { TextInput } from '../../../../web/components/TextInput';
import { brandPrimaryHover } from '../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { MonthlyRenewalAccounts } from '../MonthlyRenewalAccounts';
import { MonthlyRenewalSnapshots } from '../MonthlyRenewalSnapshots';
import {
	AdminMonthlyReportsWebsiteOptions,
	BILLING_CYCLE_OPTIONS,
	BILLING_STATUS_OPTIONS,
	BillingCycle,
	BillingStatusOptions,
	IIndustryOptions,
	INDUSTRY_OPTIONS,
	IWebsiteCriteria,
	SUBSCRIPTION_LENGTH_OPTIONS,
	SubscriptionLengthOptionCriteriaMap,
	SubscriptionLengthOptions,
	WEBSITE_OPTIONS,
} from './filterOptions';
import { appendBillingCycleCriteria, appendWebsiteCriteria } from './filterUtilities';
import { styleSheet } from './styles';

const monthyReportsRenewalParamsSchema = yup.object().shape({
	month: yup.number(),
	year: yup.number(),
	csmId: yup.string(),
	websiteFilter: yup.string().oneOf(Object.values(AdminMonthlyReportsWebsiteOptions)),
	industry: yup.string().oneOf(Object.values(IIndustryOptions)),
	billing: yup.array().of(yup.mixed<BillingCycle>().oneOf(Object.values(BillingCycle) as BillingCycle[])),
	subscriptionLength: yup.string().oneOf(Object.values(SubscriptionLengthOptions)),
	unassignedCsm: yup.boolean(),
	billingStatus: yup.string().oneOf(Object.values(BillingStatusOptions)),
	accountId: yup.string(),
});

export const ReportsMonthlyRenewal = observer(() => {
	const location = useLocation();
	const history = useHistory();
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
	const {
		billing,
		industry,
		subscriptionLength,
		csmId,
		websiteFilter,
		month,
		year,
		unassignedCsm,
		billingStatus,
		accountId,
	} = monthyReportsRenewalParamsSchema.cast({
		billing: searchParams.get('billing')
			? searchParams
					.get('billing')
					.split(',')
					.map(x => parseInt(x))
			: [BillingCycle.All],
		industry: searchParams.get('industry') ?? IIndustryOptions.All,
		subscriptionLength: searchParams.get('subscriptionLength') ?? SubscriptionLengthOptions.All,
		csmId: searchParams.get('csmId') ?? '',
		websiteFilter: searchParams.get('websiteFilter') ?? AdminMonthlyReportsWebsiteOptions.All,
		month: searchParams.get('month') ? parseInt(searchParams.get('month'), 10) : moment().month() + 1,
		year: searchParams.get('year') ? parseInt(searchParams.get('year'), 10) : moment().year(),
		unassignedCsm: searchParams.get('unassignedCsm') === 'true',
		billingStatus: searchParams.get('billingStatus') ?? BillingStatusOptions.All,
		accountId: searchParams.get('accountId') ?? '',
	});
	const setParam = React.useCallback(
		function (paramName: string, paramValue: string) {
			const newSearchParams = new URLSearchParams(searchParams);
			if (paramValue) {
				newSearchParams.set(paramName, paramValue);
			} else {
				newSearchParams.delete(paramName);
			}
			history.replace({ pathname: location.pathname, search: newSearchParams.toString() });
		},
		[history, location.pathname, searchParams]
	);
	const setBilling = (value: BillingCycle[]) => setParam('billing', value.join(','));
	const setIndustry = (value: IIndustryOptions) => setParam('industry', value);
	const setSubscriptionLength = (value: SubscriptionLengthOptions) => setParam('subscriptionLength', value);
	const setCSMId = (value: string) => setParam('csmId', value);
	const setWebsiteFilter = (value: string) => setParam('websiteFilter', value);
	const setUnassignedCsm = (value: boolean) => setParam('unassignedCsm', value ? 'true' : null);
	const setBillingStatus = (value: BillingStatusOptions) => setParam('billingStatus', value);
	const setAccountId = (value: string) => setParam('accountId', value);
	const [exportPromise, setExportPromise] = React.useState<Promise<Api.ISystemJob>>();

	const [accountsVM] = React.useState(new AccountsAdminViewModel(userSession));
	const shouldShowSnapshot = React.useMemo(() => {
		const selectedMonth = moment(`${month}-${year}`, 'MM-YYYY');
		const currentMonth = moment(`${moment().month() + 1}-${moment().year()}`, 'MM-YYYY');
		return selectedMonth.isSameOrBefore(currentMonth);
	}, [month, year]);
	const apiDate = React.useMemo(() => {
		return moment(`${month}-${year}`, 'MM-YYYY').format('YYYY-MM');
	}, [month, year]);
	const titleDate = React.useMemo(() => {
		return moment(`${month}-${year}`, 'MM-YYYY').format('MMMM YYYY');
	}, [month, year]);
	const csmUserQuery = useUserQuery({
		id: csmId,
		enabled: Boolean(csmId),
	});

	const onBillingFilterClose = (selectedOptions: ISelectOption<BillingCycle>[]) => {
		setBilling(selectedOptions.map(x => x.dataContext));
	};

	const getBillingFilterOptionsTitle = React.useCallback((selectedOptions: ISelectOption<BillingCycle>[]) => {
		if (selectedOptions.find(option => option.dataContext === BillingCycle.All)) {
			return <div>Show all billing cycles</div>;
		}
		return <div>{`Renewal period: ${selectedOptions.map(option => option.dataContext).join(', ')} months`}</div>;
	}, []);

	const onSubscriptionLengthFilterClose = (option: ISelectOption<SubscriptionLengthOptions>) => {
		if (option.dataContext !== subscriptionLength) {
			setSubscriptionLength(option.dataContext);
		}
	};

	const onIndustryFilterClose = (option: ISelectOption<IIndustryOptions>) => {
		if (option.dataContext !== industry) {
			setIndustry(option.dataContext);
		}
	};

	const onWebsiteFilterClose = (option: ISelectOption<AdminMonthlyReportsWebsiteOptions>) => {
		if (option.dataContext !== websiteFilter) {
			setWebsiteFilter(option.dataContext);
		}
	};

	const onBillingStatusFilterClose = (option: ISelectOption<BillingStatusOptions>) => {
		if (option.dataContext !== billingStatus) {
			setBillingStatus(option.dataContext);
		}
	};

	const launchQuickSight = (dashboardId: string, p?: Record<string, string>) => () => {
		const viewModel = new QuickSightDashboardViewModel(userSession, dashboardId, null, p);
		const locationState: ILocationState<QuickSightDashboardViewModel, any> = {
			viewModel,
		};
		fullscreenModal.history.push('/quicksight/dashboard', locationState);
	};

	const onExportAccountsModalRequestClose = React.useCallback(() => {
		setExportPromise(undefined);
	}, []);
	const activeFilter = React.useMemo((): IAccountFilterCriteria => {
		const criteria: IAccountFilterCriteria = {
			criteria: [
				{
					property: AccountFilterCriteriaProperty.RenewalMonth,
					value: apiDate,
				},
				{
					property: AccountFilterCriteriaProperty.AccountType,
					value: 'business',
				},
				{
					property: AccountFilterCriteriaProperty.BillingType,
					value: Api.BillingType.Paid,
				},
			],
			op: Api.FilterOperator.And,
		};
		// Handle Account ID filtering
		if (accountId && accountId.trim()) {
			criteria.criteria.push({
				property: AccountFilterCriteriaProperty.Id,
				value: accountId.trim(),
			});
		}
		// Handle SuccessManager filtering - for specific CSM ID only
		// (Unassigned CSM filtering is now handled client-side)
		if (csmId) {
			criteria.criteria.push({
				property: AccountFilterCriteriaProperty.SuccessManager,
				value: csmId,
			});
		}

		if (industry !== IIndustryOptions.All) {
			criteria.criteria.push({
				op: Api.FilterOperator.And,
				property: AccountFilterCriteriaProperty.Industry,
				value: industry,
			});
		}

		if (shouldShowSnapshot) {
			if (subscriptionLength !== SubscriptionLengthOptions.All) {
				criteria.criteria.push({
					property: AccountFilterCriteriaProperty.SubscriptionLength,
					value: SubscriptionLengthOptionCriteriaMap[subscriptionLength],
				});
			}
		} else if (subscriptionLength !== SubscriptionLengthOptions.All) {
			const monthCriteria: IAccountFilterCriteria = {
				property: AccountFilterCriteriaProperty.CreationMonth,
				value: moment()
					.set('month', month - 1)
					.set('year', year)
					.subtract(23, 'months')
					.toISOString(),
			};

			if (subscriptionLength === SubscriptionLengthOptions.OneYear) {
				monthCriteria.op = Api.FilterOperator.Gt;
			} else if (subscriptionLength === SubscriptionLengthOptions.TwoYears) {
				monthCriteria.op = Api.FilterOperator.Lt;
			}

			criteria.criteria.push(monthCriteria);
		}

		appendBillingCycleCriteria({
			billingCycleFilter: billing,
			criteria: criteria.criteria,
		});
		appendWebsiteCriteria({
			websiteFilter: (
				{
					[AdminMonthlyReportsWebsiteOptions.All]: {
						property: AccountFilterCriteriaProperty.Websites,
						value: null,
					},
					[AdminMonthlyReportsWebsiteOptions.WithWebsites]: {
						property: AccountFilterCriteriaProperty.Websites,
						value: true,
					},
					[AdminMonthlyReportsWebsiteOptions.WithoutWebsites]: {
						property: AccountFilterCriteriaProperty.Websites,
						value: false,
					},
				} as Record<AdminMonthlyReportsWebsiteOptions, IWebsiteCriteria>
			)[websiteFilter],
			criteria: criteria.criteria,
		});
		return criteria;
	}, [
		billing,
		industry,
		subscriptionLength,
		csmId,
		websiteFilter,
		month,
		year,
		shouldShowSnapshot,
		apiDate,
		accountId,
	]);

	const exportToCSV = () => {
		if (!exportPromise) {
			setExportPromise(accountsVM.exportAccounts([activeFilter], shouldShowSnapshot));
		}
	};

	const handleMonthChange = (newDate: Date) => {
		const m = moment(newDate);
		const search = new URLSearchParams(location.search);
		search.set('month', String(m.month() + 1));
		search.set('year', String(m.year()));
		history.replace(`${location.pathname}?${search.toString()}`);
	};

	const onAutocompleteItemSelectedCustomerSuccessRep = (user: Api.IUser): void => {
		setCSMId(user.id);
	};

	const clearMonthFilter = () => {
		const currentMonth = moment().month() + 1;
		const currentYear = moment().year();
		const search = new URLSearchParams(location.search);
		search.set('month', String(currentMonth));
		search.set('year', String(currentYear));
		history.replace(`${location.pathname}?${search.toString()}`);
	};

	const clearCSM = (): void => {
		const search = new URLSearchParams(location.search);
		search.delete('csmId');
		const newSearch = search.toString();
		history.push(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`);
	};
	const getSelectedIndustry = () => {
		return industry !== IIndustryOptions.All ? industry : 'Show all Industries';
	};

	const clearAllFilters = (): void => {
		history.replace(`/reports/monthly`);
	};

	return (
		<div className={`${css(styleSheet.container)}`}>
			<Portal destination={MastheadPortalId}>
				<Breadcrumb>
					<BreadcrumbList>
						<BreadcrumbItem>
							<BreadcrumbLink asChild>
								<NavLink to='/reports'>Reports</NavLink>
							</BreadcrumbLink>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
						<BreadcrumbItem>
							<BreadcrumbPage>Monthly Renewals</BreadcrumbPage>
						</BreadcrumbItem>
					</BreadcrumbList>
				</Breadcrumb>
			</Portal>
			<div className={css(styleSheet.searchSection)}>
				<div className={css(styleSheet.search)}>
					<AutoCompleteSearchField
						anchorClassName={css(styleSheet.searchBox)}
						clearSearchFieldAfterSelectingItem={true}
						dropdownContentClassName={css(styleSheet.autocompleteBox)}
						inputId='employeeSearchBox1'
						inputProps={{ placeholder: 'Search Success Peep' }}
						leftAccessory={
							<span className={css(styleSheet.iconSpan)}>
								<SearchIcon fillColor='#CCC' />
							</span>
						}
						onItemSelected={onAutocompleteItemSelectedCustomerSuccessRep}
						resultsLimit={5}
						type={Api.ResourceAutoCompleteViewModelType.User}
					/>
					<Popover>
						<PopoverTrigger asChild>
							<button>
								<TextInput
									inputClassName={css(styleSheet.dateInput)}
									inputId='reports-month-filter'
									leftAccessory={<CalendarDateIcon opacity={0.6} />}
									readOnly={true}
									type='text'
									value={titleDate}
									stopClickPropagation={false}
								/>
							</button>
						</PopoverTrigger>
						<PopoverContent>
							<DayPicker
								className={css(styleSheet.dayPicker)}
								initialMonth={moment(`${month}-${year}`, 'MM-YYYY').toDate()}
								onMonthChange={handleMonthChange}
							/>
							<PopoverArrow />
						</PopoverContent>
					</Popover>
					<DeprecatedSelect
						dropdownStyles={[styleSheet.dropdown]}
						onOptionClick={onSubscriptionLengthFilterClose}
						options={SUBSCRIPTION_LENGTH_OPTIONS}
						selectedOption={SUBSCRIPTION_LENGTH_OPTIONS.find(x => x.dataContext === subscriptionLength)}
						styles={[styleSheet.triggerContainer]}
						triggerStyles={[styleSheet.trigger]}
					/>
					<DeprecatedSelect
						dropdownStyles={[styleSheet.dropdown]}
						onOptionClick={onIndustryFilterClose}
						options={INDUSTRY_OPTIONS}
						selectedOptionTitle={getSelectedIndustry()}
						styles={[styleSheet.triggerContainer]}
						triggerStyles={[styleSheet.trigger]}
					/>
					<MultiSelect
						dropdownStyles={[styleSheet.dropdown]}
						onClose={onBillingFilterClose}
						options={BILLING_CYCLE_OPTIONS}
						selectAllIfNoneSelected={true}
						selectedOptions={BILLING_CYCLE_OPTIONS.filter(o => billing.includes(o.dataContext))}
						styles={[styleSheet.triggerContainer]}
						triggerStyles={[styleSheet.trigger]}
						selectedOptionsTitle={getBillingFilterOptionsTitle}
					/>{' '}
					<DeprecatedSelect
						dropdownStyles={[styleSheet.dropdown]}
						onOptionClick={onWebsiteFilterClose}
						options={WEBSITE_OPTIONS}
						selectedOption={WEBSITE_OPTIONS.find(o => o.dataContext === websiteFilter)}
						styles={[styleSheet.triggerContainer]}
						triggerStyles={[styleSheet.trigger]}
					/>{' '}
					{shouldShowSnapshot && (
						<DeprecatedSelect
							dropdownStyles={[styleSheet.dropdown]}
							onOptionClick={onBillingStatusFilterClose}
							options={BILLING_STATUS_OPTIONS}
							selectedOption={BILLING_STATUS_OPTIONS.find(o => o.dataContext === billingStatus)}
							styles={[styleSheet.triggerContainer]}
							triggerStyles={[styleSheet.trigger]}
						/>
					)}{' '}
					<TextInput
						inputClassName={css(styleSheet.dateInput)}
						inputId='account-id-filter'
						placeholder='Account Id'
						type='text'
						value={accountId}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAccountId(e.target.value)}
					/>
					<div className={css(styleSheet.checkboxContainer)}>
						<Checkbox
							id='unassigned-csm-checkbox'
							checked={unassignedCsm}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUnassignedCsm(e.target.checked)}
						>
							<span>Unassigned CSM</span>
						</Checkbox>
					</div>
				</div>
				<div>
					<button className={css(styleSheet.exportButton)} onClick={exportToCSV}>
						<ExportIcon fillColor={brandPrimaryHover} />
						<span style={{ marginLeft: 6 }}>Export to CSV</span>
					</button>
					{!!exportPromise && (
						<ExportConfirmationModal
							promise={exportPromise}
							modalProps={{
								isOpen: !!exportPromise,
								onRequestClose: onExportAccountsModalRequestClose,
							}}
						/>
					)}
				</div>
			</div>
			<div className={css(styleSheet.titleSection)}>
				<div className={css(styleSheet.title)}>
					{'Monthly Renewal: '}
					{csmUserQuery?.data ? getDisplayName(csmUserQuery.data) + ' - ' : ''}
					{titleDate}
					<div
						className={css(styleSheet.quickSightLink)}
						onClick={launchQuickSight(QuickSightDashboardViewModel.MonthlyRenewalId)}
					>
						<QuickSightIcon />
						<span className={css(styleSheet.quickSightText)}> View Churn Data in QuickSight</span>
					</div>
				</div>{' '}
				<div className={css(styleSheet.clearButtons)}>
					{(!!csmId ||
						!!accountId ||
						unassignedCsm ||
						!billing.some(value => value === BillingCycle.All) ||
						industry !== IIndustryOptions.All ||
						subscriptionLength !== SubscriptionLengthOptions.All ||
						billingStatus !== BillingStatusOptions.All) && (
						<button
							className={css(styleSheet.clearFilter, baseStyleSheet.ctaButtonDestructiveSmall)}
							onClick={clearAllFilters}
						>
							<span>Clear All Filters</span>
						</button>
					)}{' '}
					{!!csmId && (
						<button
							className={css(styleSheet.clearFilter, baseStyleSheet.ctaButtonDestructiveSmall)}
							onClick={clearCSM}
						>
							<span>Clear CSM</span>
						</button>
					)}
					{!!unassignedCsm && (
						<button
							className={css(styleSheet.clearFilter, baseStyleSheet.ctaButtonDestructiveSmall)}
							onClick={() => setUnassignedCsm(false)}
						>
							<span>Clear Unassigned Filter</span>
						</button>
					)}
					{(month !== moment().month() + 1 || year !== moment().year()) && (
						<button
							className={css(styleSheet.clearFilter, baseStyleSheet.ctaButtonDestructiveSmall)}
							onClick={clearMonthFilter}
						>
							<span>Go to Current Month</span>
						</button>
					)}
				</div>
			</div>{' '}
			{shouldShowSnapshot ? (
				<MonthlyRenewalSnapshots
					filter={activeFilter}
					unassignedCsmFilter={unassignedCsm}
					billingStatusFilter={billingStatus}
				/>
			) : (
				<MonthlyRenewalAccounts filter={activeFilter} unassignedCsmFilter={unassignedCsm} />
			)}
		</div>
	);
});

export * from './filterOptions';
export * from './filterUtilities';
